import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useDialog } from "../../hooks/UseDialog";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Typography,
    useTheme,
} from "@mui/material";
import Fetch from "../../services/Fetch";
import { useNavigate, useParams } from "react-router-dom";
import AlertDialog from "../../components/DialogView";
import SnackbarAlert from "../../components/SnackBar";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import QuizIcon from "@mui/icons-material/Quiz";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import { useConstants } from "../../hooks/UseConstants";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";

function questionTypeLabel(type, isAr) {
    if (type === "multiple_options") return isAr ? "اختيار من متعدد" : "Multiple choice";
    if (type === "true_false") return isAr ? "صح أو خطأ" : "True or false";
    return type;
}

function getTrueFalseAnswerLabel(question, isAr) {
    const correct = (question.options || []).find((option) => option.is_correct);
    if (!correct) return isAr ? "الإجابة: غير محددة" : "Answer: not set";
    const isTrue = correct.text_en === "True" || correct.text_ar === "صح";
    return isTrue ? (isAr ? "الإجابة: صح" : "Answer: True") : (isAr ? "الإجابة: خطأ" : "Answer: False");
}

function Questions() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { open, title, description, setDialog, setOpen } = useDialog();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [exam, setExam] = useState(null);
    const [questionId, setQuestionId] = useState("");
    const param = useParams();
    const navigate = useNavigate();

    const createQuestionPath = `/courses/${param.course_id}/exams/${param.exam_id}/create-question`;
    const courseDetailsPath = `/course-details/${param.course_id}`;

    const examName = useMemo(() => {
        if (!exam) return "";
        return isAr ? exam.name_ar : exam.name_en;
    }, [exam, isAr]);

    const questions = exam?.questions || [];
    const totalMarks = useMemo(
        () => questions.reduce((sum, q) => sum + Number(q.full_mark || 0), 0),
        [questions]
    );

    const getExam = async () => {
        setGetWait(true);
        const result = await Fetch(
            `${host}/courses/${param.course_id}/exams/${param.exam_id}/show`,
            "GET",
            null
        );
        if (result.status === 200) {
            setExam(result.data.data);
        }
        setGetWait(false);
    };

    const deleteQuestion = async () => {
        setSendWait(true);
        const result = await Fetch(
            `${host}/teacher/courses/exams/questions/${questionId}/delete`,
            "DELETE",
            null
        );

        if (result.status === 200) {
            setExam((prev) => ({
                ...prev,
                questions: prev.questions.filter((item) => item.id !== questionId),
            }));
            setOpen(false);
            setSnackBar("success", isAr ? "تم حذف السؤال" : "Question deleted");
            setQuestionId("");
        }

        setSendWait(false);
    };

    useEffect(() => {
        getExam();
    }, [param.course_id, param.exam_id]);

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <PageHero
                    title={isAr ? "أسئلة الاختبار" : "Exam questions"}
                    subtitle={isAr ? "إدارة أسئلة الاختبار وخيارات الإجابة" : "Manage exam questions and answer options"}
                    icon={<QuizIcon />}
                    actions={
                        <>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(createQuestionPath)}
                                sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                            >
                                {isAr ? "إضافة سؤال" : "Add question"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(courseDetailsPath)}
                                sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                            >
                                {isAr ? "عودة للدورة" : "Back to course"}
                            </Button>
                        </>
                    }
                />

                {exam ? (
                    <ScrollReveal>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                                mb: 3,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: theme.palette.background.paper,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isAr ? "الاختبار" : "Exam"}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }}>
                                    {examName}
                                </Typography>
                            </Box>
                            <Chip icon={<QuizIcon />} label={`${isAr ? "الأسئلة" : "Questions"}: ${questions.length}`} sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }} />
                            <Chip icon={<TimerOutlinedIcon />} label={`${exam.duration_minutes || 30} ${isAr ? "د" : "min"}`} />
                            <Chip icon={<PercentOutlinedIcon />} label={`${isAr ? "النجاح" : "Pass"}: ${exam.pass_percentage || 50}%`} />
                            <Chip label={`${isAr ? "مجموع الدرجات" : "Total marks"}: ${totalMarks}`} sx={{ fontWeight: 700 }} />
                        </Box>
                    </ScrollReveal>
                ) : null}

                <DashboardPanelCard
                    title={isAr ? "قائمة الأسئلة" : "Questions list"}
                    subtitle={
                        questions.length
                            ? (isAr ? `${questions.length} سؤال في هذا الاختبار` : `${questions.length} question(s) in this exam`)
                            : (isAr ? "لا توجد أسئلة بعد" : "No questions yet")
                    }
                >
                    {questions.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <QuizIcon sx={{ fontSize: 56, color: "#94a3b8", mb: 1 }} />
                            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                                {isAr ? "لم تُضف أسئلة لهذا الاختبار بعد" : "No questions added to this exam yet"}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate(createQuestionPath)}
                                sx={{ bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                            >
                                {isAr ? "إضافة أول سؤال" : "Add first question"}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {questions.map((question, index) => (
                                <Box
                                    key={question.id}
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        alignItems: { xs: "stretch", md: "center" },
                                        gap: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                        bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                                        transition: "box-shadow 0.2s",
                                        "&:hover": { boxShadow: "0 4px 16px rgba(13,53,140,0.08)" },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2,
                                            bgcolor: "#0D358C",
                                            color: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontWeight: 800,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {index + 1}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0D358C", mb: 0.5 }}>
                                            {isAr ? question.text_ar : question.text_en}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            <Chip
                                                size="small"
                                                label={questionTypeLabel(question.type, isAr)}
                                                sx={{ bgcolor: "#eff6ff", color: "#1d4ed8" }}
                                            />
                                            <Chip
                                                size="small"
                                                label={`${isAr ? "الدرجة" : "Mark"}: ${question.full_mark}`}
                                                sx={{ bgcolor: "#f0fdf4", color: "#166534" }}
                                            />
                                            {question.type === "true_false" ? (
                                                <Chip
                                                    size="small"
                                                    label={getTrueFalseAnswerLabel(question, isAr)}
                                                    sx={{ bgcolor: "#fef9c3", color: "#854d0e", fontWeight: 700 }}
                                                />
                                            ) : null}
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flexShrink: 0 }}>
                                        {question.type === "multiple_options" ? (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<ListAltOutlinedIcon />}
                                                onClick={() => navigate(`/courses/${param.course_id}/exams/${param.exam_id}/questions/${question.id}/options`)}
                                                sx={{ bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}
                                            >
                                                {isAr ? "الخيارات" : "Options"}
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<ListAltOutlinedIcon />}
                                                onClick={() => navigate(`/courses/${param.course_id}/exams/${param.exam_id}/questions/${question.id}/options`)}
                                            >
                                                {isAr ? "عرض الإجابة" : "View answer"}
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<EditOutlinedIcon />}
                                            onClick={() => navigate(`/courses/${param.course_id}/exams/${param.exam_id}/update-question/${question.id}`)}
                                        >
                                            {isAr ? "تعديل" : "Edit"}
                                        </Button>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                setQuestionId(question.id);
                                                setDialog(
                                                    isAr ? "حذف السؤال" : "Delete question",
                                                    isAr ? "هل أنت متأكد من حذف هذا السؤال؟" : "Are you sure you want to delete this question?"
                                                );
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}

                            <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => navigate(createQuestionPath)}
                                    sx={{ bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                                >
                                    {isAr ? "إضافة سؤال جديد" : "Add new question"}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DashboardPanelCard>
            </PageContent>

            <AlertDialog
                title={title}
                description={description}
                onCancel={() => setOpen(false)}
                onConfirm={deleteQuestion}
                wait={sendWait}
                openDialog={open}
            />
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Questions;
